import { cx } from '@linaria/core';
import { useRef, useId, useMemo, useCallback, useEffect } from 'react';
import { ElementToolbar } from '@pm2/element-toolbar';
import { useNavigationManager } from '@provider/navigation';
import { useSpeechSynthesisConfig } from '@provider/speech-synthesis';
import { breakpoints } from '@ui/css';
import { SpeechButton, Tablist } from '@ui/kit';
import { ComponentFrameworkTitle, ComponentFrameworkSaveIndicator } from '@utils/component-framework';
import { assertRefObject, useElementSize, useChangeEffect, HideFor, ShowFor } from '@utils/react';
import { useCurrentTask } from '../util.get-current-task.js';
import { useTaskToHref } from '../util.task-to-href.js';
import { actionsSize, speechButtonWrapper, speechButtonSticky, selectors, contextMenu, titleWrapper, saveIndicatorPlaceholder, actionsWrapper, actions, elementToolbarPlaceholder, saveIndicatorWrapper, saveIndicator, tabList, componentWrapper, wrapper, root } from './_component.style.js';
import { BottomNavigation } from './integration.bottom-navigation.js';
import { IGuideContextMenu } from './integration.context-menu.js';
import { EmbedWrapper } from './variant.embed.js';
import { StandaloneWrapper } from './variant.standalone.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useUpdateIGuideEntryLocationMutation } from '../state/mutation.update-entry-location.js';

function IGuideComponent(props) {
  const internalRef = useRef(null);
  const ref = props.ref ?? internalRef;
  assertRefObject(ref);
  const speechConfig = useSpeechSynthesisConfig();
  const navigation = useNavigationManager();
  const actionsRef = useRef(null);
  const id = useId();
  const taskToHref = useTaskToHref();
  const updateEntry = useUpdateIGuideEntryLocationMutation(props.component.entryKey);
  const currentTask = useCurrentTask(props.component.data, props.component.entryKey);
  const tabId = useId();
  const tabs = useMemo(() => props.component.data.tasks.map((it, index) => ({
    id: it.id,
    title: it.title || `Trin ${index + 1}`
  })), [props.component.data.tasks]);
  const onTabClick = useCallback(taskId => {
    navigation.push(taskToHref(taskId));
  }, [navigation, taskToHref]);
  const showNavigationTools = props.component.data.tasks.length > 1;
  useElementSize(actionsRef, {
    axis: "vertical",
    applyTo: ref,
    varName: actionsSize.propName
  });
  useChangeEffect(() => {
    updateEntry(currentTask.task?.id);
  }, [currentTask.task?.id]);
  const withSpeechButton = speechConfig.enabled && props.context !== "embedded";
  useEffect(() => {
    if (!props.onCollapse) {
      return;
    }
    ref.current?.focus({
      preventScroll: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    className: cx(props.className, root),
    tabIndex: -1,
    children: /*#__PURE__*/jsxs("section", {
      id: id,
      className: cx(wrapper, props.context, showNavigationTools && "withNavigationTools", withSpeechButton && "withSpeechButton"),
      children: [withSpeechButton && /*#__PURE__*/jsx("div", {
        className: speechButtonWrapper,
        children: /*#__PURE__*/jsx("div", {
          className: speechButtonSticky,
          children: /*#__PURE__*/jsx(SpeechButton, {
            rootSelector: `${selectors.wrapper()}[id="${id}"]`,
            flagPlacement: "top",
            tooltipPlacement: "left"
          })
        })
      }), props.node && /*#__PURE__*/jsx(HideFor, {
        breakpoint: breakpoints.mobile.andDown(),
        className: contextMenu,
        children: /*#__PURE__*/jsx(ElementToolbar, {
          node: props.node
        })
      }), /*#__PURE__*/jsxs("header", {
        className: titleWrapper,
        children: [/*#__PURE__*/jsx("div", {
          className: cx(saveIndicatorPlaceholder)
        }), /*#__PURE__*/jsx(ComponentFrameworkTitle, {
          title: props.component.envelope.Title,
          onCollapse: props.onCollapse,
          richtextDescription: props.context === "embedded" ? props.node?.meta.richTextDescription : undefined
        }), /*#__PURE__*/jsxs("div", {
          ref: actionsRef,
          className: actionsWrapper,
          children: [/*#__PURE__*/jsx(IGuideContextMenu, {
            rootId: id,
            className: actions,
            layout: "fab",
            context: props.context,
            targetRef: ref,
            component: props.component,
            onShareWithTeacher: props.onShareWithTeacher
          }), /*#__PURE__*/jsx(ShowFor, {
            breakpoint: breakpoints.mobile.andDown(),
            children: /*#__PURE__*/jsx("div", {
              className: elementToolbarPlaceholder,
              children: props.node && /*#__PURE__*/jsx(ElementToolbar, {
                node: props.node
              })
            })
          })]
        })]
      }), /*#__PURE__*/jsxs("div", {
        className: cx(componentWrapper, props.component.data.settings.enableBackground && "solidBackground"),
        children: [/*#__PURE__*/jsx("div", {
          className: saveIndicatorWrapper,
          children: /*#__PURE__*/jsx("span", {
            className: saveIndicator,
            children: /*#__PURE__*/jsx(ComponentFrameworkSaveIndicator, {
              entryKey: props.component.entryKey
            })
          })
        }), showNavigationTools && /*#__PURE__*/jsx(Tablist, {
          baseId: tabId,
          selectedTabId: currentTask.task?.id,
          label: "Naviger mellem opgaver",
          className: tabList,
          onTabClick: onTabClick,
          tabs: tabs,
          withSeparators: true
        }), props.context === "embedded" ? /*#__PURE__*/jsx(EmbedWrapper, {
          tabId: tabId,
          component: props.component,
          onActivate: props.onActivate,
          onNavigateTo: onTabClick,
          onResetEntry: props.onResetEntry,
          context: props.context
        }) : /*#__PURE__*/jsx(StandaloneWrapper, {
          rootRef: ref,
          context: props.context,
          tabId: tabId,
          component: props.component,
          onNavigateTo: onTabClick,
          onResetEntry: props.onResetEntry
        })]
      }), props.context === "lightbox" && currentTask.task && /*#__PURE__*/jsx(BottomNavigation, {
        component: props.component,
        currentTask: currentTask.task,
        currentTaskIndex: currentTask.index,
        onNavigate: onTabClick,
        onResetEntry: props.onResetEntry,
        context: props.context
      })]
    })
  });
}

export { IGuideComponent };
//# sourceMappingURL=_component.js.map
